import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    IconButton,
    Typography,
    Alert,
    Snackbar,
} from "@mui/material";
import {
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import {
    procurementService,
    Supplier,
    CreatePurchaseRequest,
} from "../../services/procurementService";
import { productService } from "../../services/productService";
import { Product } from "../../types";
import {
    PurchaseOrderForm,
    PurchaseOrderItem,
    PurchaseOrderFormData,
    calculatePurchaseOrderTotals,
    hasDuplicateProducts,
} from "../../components/procurement/PurchaseOrderForm";

const DRAFT_STORAGE_KEY = "purchase_order_draft";

const CreatePurchaseOrder: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // Set by PurchaseOrderList when navigating here, so Back/Cancel restores
    // its filters/search/page instead of always landing on a bare list.
    const backTo =
        (location.state as { from?: string } | null)?.from ||
        "/procurement/purchases";
    const [loading, setLoading] = useState(false);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
        null,
    );
    const [expectedDeliveryDate, setExpectedDeliveryDate] =
        useState<Date | null>(null);

    const [formData, setFormData] = useState<PurchaseOrderFormData>({
        reference_number: "",
        payment_terms: "",
        shipping_cost: 0,
        discount_amount: 0,
        notes: "",
    });

    const [items, setItems] = useState<PurchaseOrderItem[]>([]);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({
        open: false,
        message: "",
        severity: "success",
    });

    const saveDraft = () => {
        const draft = {
            selectedSupplier,
            expectedDeliveryDate: expectedDeliveryDate?.toISOString(),
            formData,
            items,
            timestamp: new Date().toISOString(),
        };
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    };

    const loadDraft = () => {
        try {
            const draftStr = localStorage.getItem(DRAFT_STORAGE_KEY);
            if (!draftStr) return false;

            const draft = JSON.parse(draftStr);
            setSelectedSupplier(draft.selectedSupplier);
            if (draft.expectedDeliveryDate) {
                setExpectedDeliveryDate(new Date(draft.expectedDeliveryDate));
            }
            setFormData({
                discount_amount: 0,
                ...draft.formData,
            });
            setItems(draft.items);
            return true;
        } catch (error) {
            console.error("Error loading draft:", error);
            return false;
        }
    };

    const clearDraft = () => {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
    };

    const loadInitialData = async () => {
        try {
            const [suppliersData, productsData] = await Promise.all([
                procurementService.getSuppliers({
                    active_only: true,
                    limit: 1000,
                }),
                productService.getProducts({ active_only: true, limit: 1000 }),
            ]);
            setSuppliers(suppliersData);
            setProducts(productsData.data || []);
        } catch (error) {
            console.error("Error loading initial data:", error);
            showSnackbar("Failed to load suppliers and products", "error");
        }
    };

    useEffect(() => {
        loadInitialData();

        // Auto-restore draft if exists
        const draftStr = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (draftStr) {
            const restored = loadDraft();
            if (restored) {
                showSnackbar("Draft restored automatically", "success");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - load only once on mount

    // Auto-save draft whenever form data changes
    useEffect(() => {
        // Only save if there's meaningful data
        if (
            selectedSupplier ||
            items.length > 0 ||
            formData.reference_number ||
            formData.notes
        ) {
            const timeoutId = setTimeout(() => {
                saveDraft();
            }, 1000); // Debounce save by 1 second

            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSupplier, expectedDeliveryDate, formData, items]);

    const showSnackbar = (message: string, severity: "success" | "error") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSubmit = async () => {
        // Validation
        if (!selectedSupplier) {
            showSnackbar("Please select a supplier", "error");
            return;
        }

        if (items.length === 0) {
            showSnackbar("Please add at least one item", "error");
            return;
        }

        const invalidItems = items.filter(
            (item) =>
                !item.product_id ||
                item.quantity_ordered <= 0 ||
                (item.unit_cost <= 0 && !item.is_gift),
        );
        if (invalidItems.length > 0) {
            showSnackbar(
                "Please fill in all required fields for all items",
                "error",
            );
            return;
        }

        if (hasDuplicateProducts(items)) {
            showSnackbar(
                "The same product appears on multiple lines — merge them into one line (use Bonus Qty for free extra units) before saving.",
                "error",
            );
            return;
        }

        try {
            setLoading(true);

            // total_amount is the pure item subtotal — the server derives
            // net_payable (what's actually owed) as
            // total_amount - discount_amount + tax_amount + shipping_cost,
            // so shipping/discount must NOT be folded in here too.
            const { subtotal } = calculatePurchaseOrderTotals(items, formData);

            const purchaseRequest: CreatePurchaseRequest = {
                supplier_id: selectedSupplier.supplier_id,
                total_amount: subtotal,
                discount_amount: formData.discount_amount || 0,
                tax_amount: 0,
                items: items.map((item) => ({
                    product_id: item.product_id,
                    quantity_ordered: item.quantity_ordered,
                    quantity_received: 0,
                    quantity_remaining:
                        item.quantity_ordered + (item.bonus_quantity || 0),
                    unit_cost: item.is_gift ? 0 : item.unit_cost,
                    total_cost: item.is_gift ? 0 : item.total_cost,
                    is_gift: item.is_gift || false,
                    bonus_quantity: item.is_gift
                        ? 0
                        : item.bonus_quantity || 0,
                    quality_check_required: item.quality_check_required,
                    requested_batch_info:
                        item.requested_batch_info || undefined,
                    notes: item.notes || undefined,
                })),
                reference_number: formData.reference_number || undefined,
                payment_terms: formData.payment_terms || undefined,
                shipping_cost: formData.shipping_cost || 0,
                expected_delivery_date:
                    expectedDeliveryDate?.toISOString() || undefined,
                notes: formData.notes || undefined,
            };

            await procurementService.createPurchase(purchaseRequest);
            clearDraft(); // Clear draft after successful submission
            showSnackbar("Purchase order created successfully", "success");

            // Navigate back to purchase orders list after a short delay
            setTimeout(() => {
                navigate(backTo);
            }, 1500);
        } catch (error) {
            console.error("Error creating purchase order:", error);
            showSnackbar("Failed to create purchase order", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 0, sm: 3 } }}>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={() => navigate(backTo)} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                >
                    Create Purchase Order
                </Typography>
            </Box>

            {/* Reusable Form Component */}
            <PurchaseOrderForm
                suppliers={suppliers}
                products={products}
                selectedSupplier={selectedSupplier}
                setSelectedSupplier={setSelectedSupplier}
                expectedDeliveryDate={expectedDeliveryDate}
                setExpectedDeliveryDate={setExpectedDeliveryDate}
                formData={formData}
                setFormData={setFormData}
                items={items}
                setItems={setItems}
                mode="create"
            />

            <Box display="flex" justifyContent="space-between" gap={1.5} mt={3}>
                <Button variant="outlined" onClick={() => navigate(backTo)}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmit}
                    disabled={loading || !selectedSupplier || items.length === 0}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    {loading ? "Creating..." : "Create Purchase Order"}
                </Button>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CreatePurchaseOrder;
